import type { FC } from 'react';
import { useInView } from 'react-intersection-observer';
import { FormattedMessage } from 'react-intl';

import type { MarkerProps, MarkerVariant } from '@xing-com/marker';

import type { SimpleCompanyItem } from '../../types';

import { SimpleCompanyCardSkeleton } from './simple-company-card.skeleton';
import * as Styled from './simple-company-card.styled';

type SimpleCompanyCardSubcomponents = {
  Skeleton: typeof SimpleCompanyCardSkeleton;
};
type SimpleCompanyCardProps = {
  item: SimpleCompanyItem;
  horizontal?: boolean;
  onWasSeen?: () => void;
  onClick?: () => void;
};

export const SimpleCompanyCard: FC<SimpleCompanyCardProps> &
  SimpleCompanyCardSubcomponents = ({
  item,
  horizontal,
  onWasSeen,
  onClick,
  ...rest
}) => {
  const markers: MarkerProps[] = item.hasOpenJobs
    ? [
        {
          children: <FormattedMessage id="CP_HIRING" />,
          variant: 'highlight' as MarkerVariant,
          ellipsis: true,
        },
      ]
    : [];

  const { ref: inViewRef } = useInView({
    onChange: (inView) => {
      if (inView) onWasSeen?.();
    },
    triggerOnce: true,
  });

  return (
    <Styled.EntityCard
      ref={inViewRef}
      key={item.id}
      data-testid={`entity-card-${item.id}`}
      layout={horizontal ? 'C' : 'B'}
      to={item.companyUrl}
      profileImage={{
        profileName: item.name,
        src: item.profileImageUri,
      }}
      markers={markers}
      headline={item.name}
      copy1={item.industry}
      copy2={item.location}
      onClick={onClick}
      {...rest}
    />
  );
};

SimpleCompanyCard.Skeleton = SimpleCompanyCardSkeleton;
